import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '../hooks/apiClient';

const tg = window.Telegram?.WebApp;

const formatDate = (iso) => new Date(iso).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' });

const plural = (n, forms) => {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return forms[0];
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return forms[1];
  return forms[2];
};

// Подпись блока: что с ним сейчас
const blockState = (b) => {
  if (b.composition_pending) return ['Ждём список пачек из ИС МПТ', 'bk-wait'];
  if (b.status === 'pending' && b.completed) return ['Все пачки проданы 🎉', 'bk-ok'];
  if (b.status === 'pending') return [`Осталось ${b.days_left} ${plural(b.days_left, ['день', 'дня', 'дней'])}`, 'bk-wait'];
  if (b.status === 'confirmed') return ['Завершён', 'bk-ok'];
  if (b.status === 'expired') return ['Окно закончилось', 'bk-err'];
  return ['Отклонён', 'bk-err'];
};

// «Мои блоки» (ТЗ, п. 4): сколько пачек продано из 10 и сколько дней осталось
const Blocks = () => {
  const navigate = useNavigate();
  const [blocks, setBlocks] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    tg?.ready();
    tg?.expand();
    apiClient.get('/v1/me/blocks')
      .then((r) => setBlocks(r.data.blocks))
      .catch((e) => {
        const detail = e.response?.data?.detail;
        setError(typeof detail === 'string' && e.response?.status !== 401 ? detail : 'Не удалось загрузить блоки. Откройте их из бота');
      });
  }, []);

  const active = (blocks || []).filter((b) => b.status === 'pending');
  const finished = (blocks || []).filter((b) => b.status !== 'pending');

  const card = (b) => {
    const [state, cls] = blockState(b);
    const tracked = b.packs_total - b.packs.presold;
    const sold = b.packs.sold;
    return (
      <div className="bk-card" key={b.id}>
        <div className="bk-head">
          <div className="bk-title">{b.product}</div>
          <div className={`bk-points${b.points > 0 ? ' bk-points-plus' : ''}`}>{b.points > 0 ? `+${b.points}` : '0'}</div>
        </div>
        <div className="bk-sub">Скан {formatDate(b.scanned_at)}
          {b.tracked_until && b.status === 'pending' && <> · отслеживается до {formatDate(b.tracked_until)}</>}
        </div>
        {!b.composition_pending && tracked > 0 && (
          <>
            <div className="bk-bar">
              {Array.from({ length: tracked }, (_, i) => <span key={i} className={i < sold ? 'bk-dot bk-dot-on' : 'bk-dot'} />)}
            </div>
            <div className="bk-sub">Продано {sold} из {tracked}
              {b.packs.presold > 0 && <> · ещё {b.packs.presold} выведено до скана</>}
              {b.packs.expired > 0 && <> · не продано: {b.packs.expired}</>}
            </div>
          </>
        )}
        <div className={`bk-state ${cls}`}>{state}</div>
        {b.reason && b.status !== 'pending' && <div className="bk-reason">{b.reason}</div>}
      </div>
    );
  };

  return (
    <div className="bk-root">
      <style>{styles}</style>
      <div className="bk-header">
        <h1 className="bk-h1">Мои блоки</h1>
        <button className="bk-scan" onClick={() => navigate('/scan_qr')}>📷 Сканировать</button>
      </div>
      {error && <p className="bk-empty bk-err">{error}</p>}
      {!blocks && !error && <div className="bk-spinner" />}
      {blocks && blocks.length === 0 && (
        <p className="bk-empty">Блоков пока нет. Отсканируйте код на наклейке блока — его пачки встанут на отслеживание</p>
      )}
      {active.length > 0 && <h2 className="bk-section">На отслеживании</h2>}
      {active.map(card)}
      {finished.length > 0 && <h2 className="bk-section">Завершённые</h2>}
      {finished.map(card)}
      <button className="bk-link" onClick={() => navigate('/balance')}>💰 Баланс и история баллов</button>
    </div>
  );
};

const styles = `
  .bk-root {
    min-height: 100vh; padding: 16px 16px calc(24px + env(safe-area-inset-bottom)); box-sizing: border-box;
    background: var(--tg-theme-bg-color, #17212b); color: var(--tg-theme-text-color, #f5f5f5);
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  }
  .bk-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 12px; }
  .bk-h1 { font-size: 24px; font-weight: 700; margin: 0; }
  .bk-scan { padding: 10px 14px; border: none; border-radius: 12px; background: #10b981; color: #fff;
             font-size: 14px; font-weight: 600; cursor: pointer; }
  .bk-section { font-size: 13px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.04em;
                color: var(--tg-theme-hint-color, #8b98a5); margin: 20px 0 8px; }
  .bk-card { padding: 14px; border-radius: 16px; margin-bottom: 10px; background: var(--tg-theme-secondary-bg-color, #232e3c); }
  .bk-head { display: flex; justify-content: space-between; gap: 12px; }
  .bk-title { font-size: 15px; font-weight: 600; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .bk-points { font-weight: 700; color: var(--tg-theme-hint-color, #8b98a5); }
  .bk-points-plus { color: #34d399; }
  .bk-sub { font-size: 12px; color: var(--tg-theme-hint-color, #8b98a5); margin-top: 4px; }
  .bk-bar { display: flex; gap: 4px; margin-top: 10px; }
  .bk-dot { flex: 1; height: 8px; border-radius: 4px; background: rgba(255, 255, 255, 0.12); }
  .bk-dot-on { background: #10b981; }
  .bk-state { display: inline-block; margin-top: 8px; padding: 2px 8px; border-radius: 8px; font-size: 12px; }
  .bk-ok { background: rgba(16, 185, 129, 0.15); color: #34d399; }
  .bk-wait { background: rgba(245, 158, 11, 0.15); color: #fbbf24; }
  .bk-err { background: rgba(239, 68, 68, 0.15); color: #f87171; }
  .bk-reason { font-size: 12px; color: #f87171; margin-top: 4px; }
  .bk-empty { text-align: center; color: var(--tg-theme-hint-color, #8b98a5); margin: 32px 8px; line-height: 1.5; }
  p.bk-err { background: none; }
  .bk-link { display: block; margin: 20px auto 0; background: none; border: none; color: #10b981; font-size: 15px; cursor: pointer; }
  .bk-spinner { width: 36px; height: 36px; margin: 48px auto; border: 4px solid rgba(255, 255, 255, 0.2);
                border-top-color: #10b981; border-radius: 50%; animation: bk-spin 0.8s linear infinite; }
  @keyframes bk-spin { to { transform: rotate(360deg); } }
`;

export default Blocks;
